import { useState, useEffect } from "react";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import api from "../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const REASON_LABEL = {
  too_fast: "Too fast",
  rate_limited: "Rate limit",
  cooldown: "Cooldown",
  captcha_failed: "Security check",
};

const REASON_STYLE = {
  captcha_failed: "bg-[#FEF3E2] text-[#B45309] border-[#B45309]/30",
  too_fast: "bg-[#FBEAEA] text-[#B42318] border-[#B42318]/30",
  rate_limited: "bg-[#F3F0EB] text-[#6E675F] border-[#DDD7CF]",
  cooldown: "bg-[#F3F0EB] text-[#6E675F] border-[#DDD7CF]",
};

function timeAgo(iso) {
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" }) + " " + d.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" });
}

function ReasonChip({ reason }) {
  return (
    <span className={`inline-block text-[11px] font-semibold border rounded-none px-2.5 py-0.5 whitespace-nowrap ${REASON_STYLE[reason] || REASON_STYLE.cooldown}`}>
      {REASON_LABEL[reason] || reason}
    </span>
  );
}

function RejectionRow({ r }) {
  const secs = typeof r.elapsed_ms === "number" ? `${(r.elapsed_ms / 1000).toFixed(1)}s` : "—";
  return (
    <TableRow data-testid={`blocked-checkout-row-${r.id}`}>
      <TableCell className="whitespace-nowrap text-xs text-[#6E675F]">{timeAgo(r.created_at)}</TableCell>
      <TableCell>
        <p className="text-sm font-medium text-[#1A1A1A] truncate max-w-[112px] sm:max-w-[220px]">{r.name || "—"}</p>
        <p className="text-xs text-[#6E675F] truncate max-w-[112px] sm:max-w-[220px]">{r.email}</p>
      </TableCell>
      <TableCell>
        <ReasonChip reason={r.reason} />
        <p className="text-[11px] text-[#6E675F] mt-1 md:hidden">{secs} on page</p>
      </TableCell>
      <TableCell className="text-right text-sm whitespace-nowrap hidden md:table-cell">{secs}</TableCell>
      <TableCell className="text-right text-sm hidden md:table-cell">{r.items ?? "—"}</TableCell>
      <TableCell className="text-right text-xs text-[#6E675F] font-mono hidden md:table-cell">{r.ip || "—"}</TableCell>
    </TableRow>
  );
}

export const BlockedCheckouts = ({ refreshKey }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/order-rejections").then(({ data: d }) => setData(d)).catch(() => setData({ error: true }));
  }, [refreshKey]);

  const summary = data?.summary;
  const recent = data?.recent || [];
  const quiet = summary && summary.total_30d === 0;

  return (
    <div className={`border rounded-lg bg-white p-6 ${summary?.total_7d > 0 ? "border-[#B45309]/40" : "border-[#DDD7CF]"}`} data-testid="stats-blocked-checkouts">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] flex items-center gap-2">
          <ShieldAlert className="w-4 h-4" /> Blocked Checkouts
        </p>
        {summary && (
          <div className="flex flex-wrap gap-2 text-xs" data-testid="blocked-checkouts-summary">
            <span className="border border-[#DDD7CF] rounded-none px-3 py-1 text-[#1A1A1A]"><strong>{summary.total_7d}</strong> in 7 days</span>
            <span className="border border-[#DDD7CF] rounded-none px-3 py-1 text-[#1A1A1A]"><strong>{summary.total_30d}</strong> in 30 days</span>
            <span className="border border-[#DDD7CF] rounded-none px-3 py-1 text-[#1A1A1A]"><strong>{summary.unique_customers_7d}</strong> customer{summary.unique_customers_7d === 1 ? "" : "s"} affected (7d)</span>
          </div>
        )}
      </div>

      {!data && <p className="text-sm text-[#6E675F]">Loading…</p>}
      {data?.error && <p className="text-sm text-[#B42318]" data-testid="blocked-checkouts-error">Couldn't load the blocked-checkout log.</p>}

      {quiet && (
        <div className="flex items-start gap-3 text-sm text-[#1A1A1A]" data-testid="blocked-checkouts-empty">
          <ShieldCheck className="w-5 h-5 text-[#2E7D4F] shrink-0" />
          <span>No checkouts have been blocked in the last 30 days — every shopper who tried to place an order got through the anti-bot checks.</span>
        </div>
      )}

      {summary && !quiet && (
        <>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="blocked-checkouts-by-reason">
            {Object.entries(summary.by_reason_7d).map(([reason, n]) => (
              <span key={reason} className="inline-flex items-center gap-1.5 text-xs text-[#6E675F]">
                <ReasonChip reason={reason} /> {n}
              </span>
            ))}
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right hidden md:table-cell">Time on page</TableHead>
                  <TableHead className="text-right hidden md:table-cell">Items</TableHead>
                  <TableHead className="text-right hidden md:table-cell">IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recent.map((r) => <RejectionRow key={r.id} r={r} />)}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {data?.reasons && (
        <div className="mt-6 pt-4 border-t border-[#DDD7CF] grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-[#6E675F]" data-testid="blocked-checkouts-legend">
          {Object.entries(data.reasons).map(([k, v]) => (
            <span key={k} className="flex items-center gap-2"><ReasonChip reason={k} /> {v}</span>
          ))}
          <span className="sm:col-span-2 mt-1">A real customer showing up here more than once is worth a quick email — they may need help completing their order.</span>
        </div>
      )}
    </div>
  );
};
